import {
  Fragment,
  useEffect,
  useId,
  useState,
  ChangeEvent,
  FocusEvent,
  FocusEventHandler,
  KeyboardEvent,
  DragEvent,
  ReactNode,
  Ref,
} from 'react';
import { addHours, startOfDay } from 'date-fns';
import {
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  FormControl,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Stack,
  Chip,
  Box,
  Autocomplete,
  styled,
  Button,
  Input,
  IconButton,
  FormLabel,
  RadioGroup,
  Radio,
  useTheme,
  SxProps,
  Theme,
  AutocompleteRenderOptionState,
  PopperProps,
  SelectChangeEvent,
  AutocompleteChangeReason,
  FilterOptionsState,
} from '@mui/material';
import {
  MobileDatePicker,
  LocalizationProvider,
  DesktopDatePicker,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// TODO: Remove luxon because the date picker starts with Monday instead of Sunday.
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import ReactInputMask from 'react-input-mask';
import { GridContainer, GridItem, colors } from './ui-extension';
import { isDateValid, separator, toFileObject } from '../../shared/utils';
import { DeleteOutline } from '@mui/icons-material';

export type InputType =
  | 'text'
  | 'checkbox'
  | 'checkbox-chip'
  | 'select'
  | 'select-search'
  | 'radio'
  | 'date'
  | 'mobileDate'
  | 'number'
  | 'currency'
  | 'file'
  | 'email'
  | 'password'
  | 'tel';

export interface SelectOption {
  value: string | number;
  label?: string;
}

export interface FileAttachment {
  filename: string;
  [key: string]: unknown;
}

export interface FileChangeEvent {
  files?: File[];
  attachments?: unknown[];
  deletedFilename?: string;
}

export interface LabeledInputProps {
  type?: InputType;
  label?: string;
  name?: string;
  value?: string | number | boolean | Date | FileAttachment[] | null;
  onChange?: (
    event:
      | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent
      | { target: { value?: unknown; checked?: boolean } }
      | Date
      | FileChangeEvent,
    reason?: AutocompleteChangeReason
  ) => void;
  onInputChange?: (
    event: React.SyntheticEvent,
    value: string,
    reason: string
  ) => void;
  updateOnChangeLive?: boolean;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onFocus?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  options?: SelectOption[];
  filterOptions?: (
    options: SelectOption[],
    state: FilterOptionsState<SelectOption>
  ) => SelectOption[];
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: SelectOption,
    state: AutocompleteRenderOptionState
  ) => ReactNode;
  helperText?: string;
  disabled?: boolean;
  variant?: 'standard' | 'outlined' | 'filled';
  size?: 'small' | 'medium';
  placeholder?: string | null;
  additionalProps?: Record<string, unknown>;
  freeSolo?: boolean;
  multiple?: boolean;
  title?: string;
  error?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  groupBy?: (option: SelectOption) => string;
  labelSx?: SxProps<Theme>;
  minDate?: Date;
  maxDate?: Date;
  textSx?: SxProps<Theme>;
  disableFuture?: boolean;
  disablePast?: boolean;
  useDateFns?: boolean;
  /** Date inputs only: call onChange with null when the field is cleared. */
  allowClear?: boolean;
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom';
  sx?: SxProps<Theme>;
  currencyHeaderSx?: SxProps<Theme>;
  currencyDollarSignSx?: SxProps<Theme>;
  dateSx?: SxProps<Theme>;
  checkboxSx?: SxProps<Theme>;
  popperProps?: Partial<PopperProps>;
  required?: boolean;
  multiline?: boolean;
  multilineRows?: number;
  currencyHeaderXs?: number | null;
  hideDollarSign?: boolean;
  characterLimit?: number;
  getOptionDisabled?: (option: SelectOption) => boolean;
  getValidationError?:
    | ((
        value: unknown,
        validationData: Record<string, unknown>
      ) => string | null)
    | null;
  validationData?: Record<string, unknown>;
  mask?: string | null;
  horizontalLayout?: boolean;
}

interface ValidationProps {
  error?: boolean;
  helperText?: string | null;
}

export const LabeledInput = ({
  type,
  label: labelProp,
  name,
  value: valueProp,
  onChange,
  updateOnChangeLive,
  onInputChange,
  onBlur,
  onFocus,
  options,
  filterOptions,
  renderOption,
  helperText,
  disabled = false,
  variant = 'standard',
  size = 'small',
  placeholder = null,
  additionalProps,
  freeSolo,
  multiple,
  title: titleProp,
  error,
  inputRef,
  groupBy,
  labelSx = {},
  minDate,
  maxDate,
  textSx = {},
  disableFuture = false,
  disablePast = false,
  useDateFns = false,
  allowClear = false,
  labelPlacement = 'end',
  sx = {},
  currencyHeaderSx = {},
  currencyDollarSignSx = {},
  dateSx = {},
  checkboxSx = { padding: 0, margin: '9px' },
  popperProps,
  required = false,
  multiline = false,
  multilineRows = 1,
  currencyHeaderXs = null,
  hideDollarSign = false,
  characterLimit,
  getOptionDisabled = () => false,
  getValidationError = null,
  validationData = {},
  mask = null,
  horizontalLayout = false,
}: LabeledInputProps) => {
  const theme = useTheme();

  let value = valueProp === undefined ? '' : valueProp;
  let title = titleProp || labelProp;
  let label = labelProp;

  const id = useId();
  const inputId = `${name || id}-${type}`;
  const labelId = `${inputId}-label`;

  const useValidation = !!getValidationError;

  if (useValidation && label) {
    label = `* ${label}`;
  }

  // Use a local state to track input to avoid unnecessary re-renders.
  const useResponsiveInput = !onBlur;

  const [inputValue, setInputValue] = useState<string>('');
  const [isDragActive, setIsDragActive] = useState(false);

  useEffect(() => {
    setInputValue(value as string);
  }, [value]);

  const responseInputProps = {
    value: useResponsiveInput ? inputValue : value,
    onChange: useResponsiveInput
      ? (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          setInputValue(e.target.value);
          if (updateOnChangeLive) {
            onChange(e);
          }
        }
      : onChange,
    onBlur: useResponsiveInput
      ? (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
          (
            onChange as
              | ((
                  e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                ) => void)
              | undefined
          )?.(
            e as unknown as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
          );
        }
      : onBlur,
  };

  const getValidationProps = (val: unknown): ValidationProps =>
    useValidation
      ? {
          error: !!getValidationError(val, validationData),
          helperText: getValidationError(val, validationData),
        }
      : {
          error,
          helperText,
        };

  if (type == 'checkbox') {
    return (
      <FormControlLabel
        labelPlacement={labelPlacement}
        disabled={disabled}
        title={title}
        sx={{ marginRight: 0, ...labelSx }}
        control={
          <Checkbox
            id={inputId}
            checked={value === 'N' ? false : !!value}
            onChange={
              onChange as (event: ChangeEvent<HTMLInputElement>) => void
            }
            size={size}
            sx={checkboxSx}
            inputRef={inputRef}
            {...additionalProps}
          />
        }
        label={
          <Typography
            variant='body2'
            sx={{ marginBottom: 0, ...labelSx }}
            id={labelId}
          >
            {label}
          </Typography>
        }
      />
    );
  }

  if (type == 'checkbox-chip') {
    return (
      <Chip
        title={title}
        size={size}
        label={label}
        color={value ? 'primary' : 'default'}
        disabled={disabled}
        sx={{ height: '20px' }}
        onClick={() => {
          (
            onChange as
              | ((e: { target: { checked: boolean } }) => void)
              | undefined
          )?.({ target: { checked: !value } });
        }}
        {...additionalProps}
      />
    );
  }

  if (type === 'select-search') {
    const renderTags = (
      tagValue: (string | number)[],
      getTagProps: (params: { index: number }) => Record<string, unknown>
    ) =>
      tagValue.map((v, index) => (
        <Chip
          key={index}
          size={size}
          label={options?.filter((o) => o.value === v)[0]?.label || v}
          {...getTagProps({ index })}
        />
      ));

    // Do not show already selected.
    const valueArray = multiple
      ? (value as unknown as (string | number)[])
      : [];
    const autoCompleteOptions =
      (multiple
        ? options?.filter((o) => !valueArray.some((v) => v === o.value))
        : options) || [];

    let sortedOptions = options;
    if (groupBy && sortedOptions) {
      sortedOptions = [...sortedOptions].sort((a, b) => {
        return `${groupBy(a)}`.localeCompare(`${groupBy(b)}`);
      });
    }

    return (
      <Autocomplete<SelectOption, boolean, boolean, boolean>
        disablePortal
        title={title}
        id={inputId}
        getOptionDisabled={getOptionDisabled}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return option.label ?? String(option.value);
        }}
        value={
          freeSolo
            ? (value as string)
            : options?.filter((o) => o.value == value)[0] || null
        }
        isOptionEqualToValue={(option, val) => {
          if (typeof option === 'string' || typeof val === 'string') {
            return (
              `${
                typeof option === 'string' ? option : option.value
              }`.toLowerCase() ===
              `${typeof val === 'string' ? val : val.value}`.toLowerCase()
            );
          }
          return option.value === val.value;
        }}
        onChange={(e, selected, reason) => {
          (
            onChange as
              | ((
                  e: { target: { value?: unknown } },
                  reason?: AutocompleteChangeReason
                ) => void)
              | undefined
          )?.(
            {
              target: {
                value: multiple
                  ? (selected as SelectOption[])?.flatMap((s) =>
                      typeof s === 'string' ? s : s.value
                    )
                  : typeof selected === 'string'
                  ? selected
                  : (selected as SelectOption)?.value,
              },
            },
            reason
          );
        }}
        freeSolo={freeSolo}
        multiple={multiple}
        options={autoCompleteOptions}
        groupBy={
          groupBy
            ? (option) => (typeof option === 'string' ? '' : groupBy(option))
            : undefined
        }
        renderGroup={({ key, group, children }) => (
          <Fragment key={key}>
            <Stack>
              <Typography
                variant='caption'
                sx={{
                  fontWeight: 'bold',
                  paddingLeft: 1,
                  backgroundColor: colors.lightGray,
                  width: '100%',
                }}
              >
                {group}
              </Typography>
            </Stack>
            {children}
          </Fragment>
        )}
        renderOption={
          renderOption
            ? (props, option, state) =>
                renderOption(props, option as SelectOption, state)
            : (props, option) => (
                <Stack {...props} component='li' alignItems='flex-start'>
                  {(() => {
                    const optionLabel =
                      typeof option === 'string' ? option : option.label;
                    const tokens = optionLabel?.split(separator);
                    return tokens?.map((line, i) => {
                      const isFirstItem = i === 0;
                      return (
                        <Box
                          sx={{
                            fontWeight: isFirstItem ? 'bolder' : 'normal',
                            width: '100%',
                            fontSize: isFirstItem ? '14px' : '12px',
                          }}
                          key={i}
                        >
                          {line}
                        </Box>
                      );
                    });
                  })()}
                </Stack>
              )
        }
        disabled={disabled}
        size={size}
        onBlur={onBlur as FocusEventHandler<HTMLDivElement> | undefined}
        onFocus={onFocus as FocusEventHandler<HTMLDivElement> | undefined}
        filterOptions={
          filterOptions
            ? (options, state) =>
                filterOptions(
                  options as SelectOption[],
                  state as FilterOptionsState<SelectOption>
                ) as SelectOption[]
            : undefined
        }
        onInputChange={
          freeSolo === true && !onInputChange && !multiple
            ? (e, val) => {
                (
                  onChange as
                    | ((e: { target: { value: string } }) => void)
                    | undefined
                )?.({ target: { value: val } });
              }
            : onInputChange
        }
        renderTags={
          multiple
            ? (tagValues, getTagProps) => {
                if (!tagValues || !tagValues.length) return undefined;
                return renderTags(
                  tagValues.map((v) =>
                    typeof v === 'string' ? v : v?.value
                  ) as (string | number)[],
                  getTagProps
                );
              }
            : undefined
        }
        sx={sx}
        renderInput={(props) => (
          <TextField
            {...props}
            {...getValidationProps(value)}
            variant={variant}
            label={label}
            InputLabelProps={{ sx: labelSx }}
            size={size}
            placeholder={placeholder ?? undefined}
            inputRef={inputRef}
            required={required}
            {...additionalProps}
          />
        )}
      />
    );
  }

  if (type === 'radio') {
    const { error: radioError, helperText: radioHelperText } =
      getValidationProps(value);

    const CustomRadio = (props: Record<string, unknown>) => (
      <Radio
        size={size}
        sx={{
          padding: '1px',
          marginRight: 1,
          color: radioError ? theme.palette.error.main : undefined,
        }}
        {...props}
      />
    );

    return (
      <FormControl
        title={title}
        sx={
          radioError
            ? {
                border: `1px solid ${theme.palette.error.main}`,
                borderRadius: '5px',
                paddingLeft: '16px',
              }
            : {
                paddingLeft: '8px',
              }
        }
      >
        <FormLabel id={labelId}>{label}</FormLabel>
        {radioHelperText ? (
          <FormHelperText error={radioError}>{radioHelperText}</FormHelperText>
        ) : null}
        <RadioGroup
          id={inputId}
          name={name}
          value={value}
          onChange={onChange as (event: ChangeEvent<HTMLInputElement>) => void}
        >
          {options?.map((option, index) => (
            <FormControlLabel
              key={index}
              value={option.value}
              control={<CustomRadio />}
              label={option.label}
              disabled={getOptionDisabled?.(option)}
              slotProps={
                radioError
                  ? {
                      typography: { color: 'error' },
                    }
                  : undefined
              }
            />
          ))}
        </RadioGroup>
      </FormControl>
    );
  }

  if (type === 'date')
    // TODO: Stop using luxon and use date-fns instead.
    return (
      <LocalizationProvider
        dateAdapter={useDateFns ? AdapterDateFns : AdapterLuxon}
      >
        <Stack
          direction='row'
          display='flex'
          alignItems='center'
          sx={{ width: '100%' }}
        >
          {horizontalLayout && (
            <Typography
              sx={{
                ...labelSx,
                flexShrink: 0,
                width: '50%',
                marginRight: '5px',
              }}
              noWrap
              variant='body2'
            >
              {title}
            </Typography>
          )}
          <DesktopDatePicker
            // @ts-ignore - Using older MUI x-date-pickers API with renderInput
            sx={dateSx}
            PopperProps={popperProps ? popperProps : {}}
            label={label}
            name={name}
            title={title}
            inputFormat={'MM/dd/yyyy'}
            orientation='portrait'
            disableFuture={disableFuture}
            disablePast={disablePast}
            value={value as Date | null}
            onChange={
              useDateFns
                ? (date: Date | null) => {
                    if (!date) {
                      if (allowClear)
                        (onChange as (date: Date | null) => void)(null);
                      return;
                    }
                    // Set a consistent time to avoid server timezone issues.
                    const adjustedDate = addHours(startOfDay(date), 5);
                    (onChange as (date: Date) => void)(adjustedDate);
                  }
                : (onChange as (date: Date | null) => void)
            }
            inputRef={inputRef}
            disabled={disabled}
            minDate={minDate}
            maxDate={maxDate}
            renderInput={(params: Record<string, unknown>) => (
              <TextField
                {...params}
                {...getValidationProps(value)}
                sx={{ ...{ width: '100%' }, ...textSx }}
                variant={variant}
                label={label}
                size={size}
                {...additionalProps}
              />
            )}
          />
        </Stack>
      </LocalizationProvider>
    );

  if (type === 'mobileDate')
    // TODO: Stop using luxon and use date-fns instead.
    return (
      <LocalizationProvider
        dateAdapter={useDateFns ? AdapterDateFns : AdapterLuxon}
      >
        <MobileDatePicker
          // @ts-ignore - Using older MUI x-date-pickers API with renderInput
          sx={dateSx}
          label={label}
          name={name}
          title={title}
          inputFormat={'MM/dd/yyyy'}
          disableFuture={disableFuture}
          disablePast={disablePast}
          value={value as Date | null}
          onChange={(date: Date | null) => {
            if (!date || isNaN(date.getTime())) return;
            if (useDateFns && !isDateValid(date)) return;
            (onChange as ((date: Date) => void) | undefined)?.(date);
          }}
          inputRef={inputRef}
          disabled={disabled}
          minDate={minDate}
          maxDate={maxDate}
          renderInput={(params: Record<string, unknown>) => (
            <TextField
              {...params}
              {...getValidationProps(value)}
              sx={{ ...{ width: '100%' }, ...textSx }}
              variant={variant}
              label={label}
              size={size}
              error={error}
              {...additionalProps}
            />
          )}
        />
      </LocalizationProvider>
    );

  const selectInput =
    type === 'select'
      ? (() => {
          const { error: selectError, helperText: selectHelperText } =
            getValidationProps(value);

          return (
            <>
              <InputLabel sx={labelSx} id={labelId} error={selectError}>
                {label}
              </InputLabel>
              <Select
                labelId={labelId}
                id={inputId}
                value={value as string | number}
                label={label}
                disabled={disabled}
                onChange={
                  onChange as (
                    event: SelectChangeEvent<string | number>
                  ) => void
                }
                size={size}
                error={selectError}
                inputRef={inputRef}
                sx={sx}
                {...additionalProps}
              >
                {options?.map((o, i) => (
                  <MenuItem value={o.value} key={i}>
                    {o.label || o.value}
                  </MenuItem>
                ))}
              </Select>
              {selectHelperText ? (
                <FormHelperText error={selectError}>
                  {selectHelperText}
                </FormHelperText>
              ) : null}
            </>
          );
        })()
      : null;

  const numberInput =
    type === 'number' ? (
      <TextFieldNoArrow
        sx={{ ...{ width: '100%' }, ...textSx }}
        id={inputId}
        label={label}
        type={'number'}
        disabled={disabled}
        onFocus={onFocus}
        variant={variant}
        size={size}
        placeholder={placeholder ?? undefined}
        inputRef={inputRef}
        {...responseInputProps}
        {...getValidationProps(responseInputProps.value)}
        {...additionalProps}
      />
    ) : null;

  const currencyInput =
    type === 'currency' ? (
      <Stack>
        <Typography sx={currencyHeaderSx} variant='caption'>
          {label}
        </Typography>
        <GridContainer xs={currencyHeaderXs}>
          {hideDollarSign ? null : (
            <GridItem sx={{ paddingBottom: '4px' }}>
              <Typography sx={currencyDollarSignSx} variant='body2'>
                $
              </Typography>
            </GridItem>
          )}
          <GridItem sx={{ flex: 1 }}>
            <TextFieldNoArrow
              sx={{ ...{ width: '100%' }, ...textSx }}
              id={inputId}
              type={'number'}
              // Prevent the user from typing 'e', 'E', '+', or '-', somehow these are allowed in number type inputs.
              onKeyDown={(e: KeyboardEvent<HTMLDivElement>) =>
                ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault()
              }
              disabled={disabled}
              onFocus={onFocus}
              variant={variant}
              size={size}
              placeholder={placeholder ?? undefined}
              inputRef={inputRef}
              {...responseInputProps}
              {...getValidationProps(responseInputProps.value)}
              {...additionalProps}
            />
          </GridItem>
        </GridContainer>
      </Stack>
    ) : null;

  if (characterLimit && characterLimit > 0) {
    label = `${label || ''} (${
      inputValue.length
    }/${characterLimit} characters)`;
  }

  const createTextInput = (props: Record<string, unknown>) => {
    const inputProps = {
      ...(additionalProps?.inputProps as Record<string, unknown> | undefined),
      maxLength: characterLimit || undefined,
      name: name || undefined,
    };
    return (
      <TextField
        {...props}
        id={inputId}
        required={required}
        label={label}
        type={type}
        disabled={disabled}
        onFocus={onFocus}
        multiline={multiline}
        rows={multilineRows}
        variant={variant}
        size={size}
        sx={sx}
        placeholder={placeholder ?? undefined}
        inputRef={inputRef}
        inputProps={
          Object.values(inputProps).some((v) => !!v) ? inputProps : undefined
        }
        InputLabelProps={{ id: labelId, sx: labelSx }}
        {...getValidationProps(responseInputProps.value)}
        {...additionalProps}
      />
    );
  };

  const textInput = mask ? (
    <ReactInputMask mask={mask} disabled={disabled} {...responseInputProps}>
      {(maskInputProps: Record<string, unknown>) =>
        createTextInput(maskInputProps)
      }
    </ReactInputMask>
  ) : (
    createTextInput(responseInputProps)
  );

  const fileInput =
    type === 'file' ? (
      <Stack spacing={1}>
        <Button
          variant={
            variant === 'standard'
              ? 'text'
              : variant === 'filled'
              ? 'contained'
              : variant
          }
          disabled={disabled}
          // @ts-ignore - MUI polymorphic component prop type issue
          component='label'
          color='primary'
          size={size}
          sx={{
            border: '1px dashed #ccc',
            backgroundColor: isDragActive ? colors.darkGray : 'transparent',
            ...sx,
          }}
          onDragOver={(e: DragEvent<HTMLButtonElement>) => {
            e.preventDefault();
            setIsDragActive(true);
          }}
          onDragLeave={() => setIsDragActive(false)}
          onDrop={async (e: DragEvent<HTMLButtonElement>) => {
            setIsDragActive(false);
            e.preventDefault();

            if (!e.dataTransfer.files?.length) return;

            const attachments: unknown[] = [];
            const files: File[] = [];

            for (const file of Array.from(e.dataTransfer.files)) {
              files.push(file);
              attachments.push(await toFileObject(file));
            }

            (onChange as ((e: FileChangeEvent) => void) | undefined)?.({
              files,
              attachments,
            });
          }}
        >
          {label || 'Click or drag files to this area to upload'}
          <Input
            inputProps={{ multiple: true }}
            sx={{ display: 'none' }}
            type='file'
            hidden
            onDragOver={(e: DragEvent<HTMLInputElement>) => e.preventDefault()}
            onChange={async (e: ChangeEvent<HTMLInputElement>) => {
              if (!e.target.files?.length) return;

              const attachments: unknown[] = [];

              for (const file of Array.from(e.target.files)) {
                attachments.push(await toFileObject(file));
              }

              (onChange as ((e: FileChangeEvent) => void) | undefined)?.({
                files: Array.from(e.target.files),
                attachments,
              });
            }}
          />
        </Button>
        {Array.isArray(value)
          ? (value as FileAttachment[]).map((file, index) => {
              return (
                <Stack
                  direction={'row'}
                  alignItems='center'
                  spacing={1}
                  key={index}
                >
                  <IconButton color='error' size='small'>
                    <DeleteOutline
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        (
                          onChange as ((e: FileChangeEvent) => void) | undefined
                        )?.({ deletedFilename: file.filename });
                      }}
                    />
                  </IconButton>
                  <Typography>{file.filename}</Typography>
                </Stack>
              );
            })
          : null}
      </Stack>
    ) : null;

  return (
    <FormControl variant={variant} sx={{ width: '100%' }} title={title}>
      {selectInput || numberInput || currencyInput || fileInput || textInput}
    </FormControl>
  );
};

const TextFieldNoArrow = styled(TextField)({
  '& input[type=number]::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
});

type SetObjectFunction<T> = React.Dispatch<React.SetStateAction<T>>;

export const bindTextInput = <T extends Record<string, unknown>>(
  setObject: SetObjectFunction<T>,
  pathInput: string,
  updateValue?: (value: string) => string
) => {
  const path = pathInput.split('.');
  const variableName = path.pop()!;

  return (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = updateValue?.(e.target.value) || e.target.value;

    setObject((prev) => {
      let reference: Record<string, unknown> = prev as Record<string, unknown>;

      for (const p of path) {
        if (reference[p] === undefined) {
          reference[p] = {};
        }
        reference = reference[p] as Record<string, unknown>;
      }

      reference[variableName] = value;

      return { ...prev };
    });
  };
};

export const bindTimeInput = <T extends Record<string, unknown>>(
  setObject: SetObjectFunction<T>,
  pathInput: string
) => {
  const path = pathInput.split('.');
  const variableName = path.pop()!;

  return (date: Date | null) => {
    setObject((prev) => {
      let reference: Record<string, unknown> = prev as Record<string, unknown>;

      for (const p of path) {
        reference = reference[p] as Record<string, unknown>;
      }

      reference[variableName] =
        (date && date.toLocaleTimeString('it-IT')) || '';
      return { ...prev };
    });
  };
};
